/**
 * Missing Field Analysis for Correspondence Packages
 *
 * Analyzes validation results to categorize missing fields and generate
 * actionable CTAs. Mirrors the pattern from HIPAA MissingDataBanner but
 * adapted for the correspondence package context.
 */
import type {
  CorrespondenceChannel,
  Transmission,
} from '@/services/api/correspondenceService';

import type { MissingFieldInfo, PackageValidationStatus } from '../types';

// ============================================================================
// Types
// ============================================================================

export type MissingFieldCategory =
  | 'transmission' // Recipient/transmission details missing
  | 'client' // Client-related fields (clientName, clientDOB, etc.)
  | 'matter' // Matter-level fields (dateOfLoss, etc.)
  | 'recipient' // Recipient address/contact details in templates
  | 'other'; // Uncategorized fields

export interface FieldCategoryBreakdown {
  category: MissingFieldCategory;
  fields: MissingFieldInfo[];
  count: number;
}

export interface TransmissionCTA {
  text: string;
  description: string;
  action: 'selectRecipient' | 'setTransmission';
}

export interface MissingFieldAnalysis {
  /** Whether transmission/recipient setup is incomplete */
  needsTransmission: boolean;
  /** Whether client-related fields are missing */
  needsClientInfo: boolean;
  /** CTA for transmission panel (null if complete) */
  transmissionCTA: TransmissionCTA | null;
  /** Fields grouped by category for detailed display */
  categorizedFields: FieldCategoryBreakdown[];
  /** Total distinct issue categories */
  issueCount: number;
  /** Total count of missing fields across all components */
  totalMissingFields: number;
}

// ============================================================================
// Context Key Categorization
// ============================================================================

const CLIENT_CONTEXT_PREFIXES = ['client', 'primaryClient'] as const;

const MATTER_CONTEXT_PREFIXES = [
  'matter',
  'firm',
  'attorney',
  'incident',
  'dateOfLoss',
] as const;

const RECIPIENT_CONTEXT_PREFIXES = ['recipient'] as const;

/** Categorize a missing field by its context key */
function categorizeField(field: MissingFieldInfo): MissingFieldCategory {
  const key = field.contextKey?.toLowerCase() ?? '';

  if (
    CLIENT_CONTEXT_PREFIXES.some((prefix) =>
      key.startsWith(prefix.toLowerCase()),
    )
  ) {
    return 'client';
  }
  if (
    MATTER_CONTEXT_PREFIXES.some((prefix) =>
      key.startsWith(prefix.toLowerCase()),
    )
  ) {
    return 'matter';
  }
  if (
    RECIPIENT_CONTEXT_PREFIXES.some((prefix) =>
      key.startsWith(prefix.toLowerCase()),
    )
  ) {
    return 'recipient';
  }
  return 'other';
}

// ============================================================================
// Transmission CTA Generation
// ============================================================================

/** Get channel-specific CTA text for incomplete transmission */
function getChannelSpecificCTA(
  channel: CorrespondenceChannel,
): TransmissionCTA {
  switch (channel) {
    case 'fax':
      return {
        text: 'Set Fax Number',
        description: 'Enter the fax number for this recipient.',
        action: 'setTransmission',
      };
    case 'email':
      return {
        text: 'Set Email Address',
        description: 'Enter the email address for this recipient.',
        action: 'setTransmission',
      };
    case 'mailed_letter':
    case 'mailed_cheque':
      return {
        text: 'Set Mailing Address',
        description: 'Enter the mailing address for this recipient.',
        action: 'setTransmission',
      };
    default:
      return {
        text: 'Complete Delivery Details',
        description: 'Configure how this package will be delivered.',
        action: 'setTransmission',
      };
  }
}

/** Check if transmission details are complete for the selected channel */
function isTransmissionComplete(
  transmission: Partial<Transmission> | null,
): boolean {
  if (!transmission?.type) return false;

  switch (transmission.type) {
    case 'fax':
      return !!transmission.fax_number;
    case 'email':
      return !!transmission.email;
    case 'mailed_letter':
    case 'mailed_cheque':
      return !!(
        transmission.address?.address_line1 &&
        transmission.address?.city &&
        transmission.address?.state &&
        transmission.address?.zip_code
      );
    default:
      return false;
  }
}

/** Generate transmission CTA based on current state */
export function getTransmissionCTA(
  recipientName: string | null | undefined,
  transmission: Partial<Transmission> | null,
): TransmissionCTA | null {
  // No recipient selected
  if (!recipientName) {
    return {
      text: 'Select Recipient',
      description: 'Choose who will receive this correspondence.',
      action: 'selectRecipient',
    };
  }

  // No channel selected yet
  if (!transmission?.type) {
    return {
      text: 'Choose Delivery Method',
      description: 'Select how to send this package (fax, email, or mail).',
      action: 'setTransmission',
    };
  }

  // Channel selected but details incomplete
  if (!isTransmissionComplete(transmission)) {
    return getChannelSpecificCTA(transmission.type);
  }

  // Transmission is complete
  return null;
}

// ============================================================================
// Main Analysis Function
// ============================================================================

/**
 * Analyze missing fields and transmission state to generate actionable insights.
 *
 * Returns categorized fields and CTAs for the validation banner.
 */
export function analyzeMissingFields(
  validationStatus: PackageValidationStatus,
  recipientName: string | null | undefined,
  transmission: Partial<Transmission> | null,
): MissingFieldAnalysis {
  // Collect all missing fields from components
  const allMissingFields: MissingFieldInfo[] =
    validationStatus.componentResults.flatMap((result) =>
      Object.values(result.missingFields),
    );

  // Categorize fields
  const fieldsByCategory = allMissingFields.reduce((acc, field) => {
    const category = categorizeField(field);
    const existing = acc.get(category) ?? [];
    acc.set(category, [...existing, field]);
    return acc;
  }, new Map<MissingFieldCategory, MissingFieldInfo[]>());

  // Build category breakdown
  const categorizedFields: FieldCategoryBreakdown[] = Array.from(
    fieldsByCategory.entries(),
  ).map(([category, fields]) => ({
    category,
    fields,
    count: fields.length,
  }));

  // Check transmission state
  const transmissionCTA = getTransmissionCTA(recipientName, transmission);
  const needsTransmission = transmissionCTA !== null;

  // Check for client info issues
  const clientFields = fieldsByCategory.get('client') ?? [];
  const needsClientInfo = clientFields.length > 0;

  // Count distinct issue categories
  const issues = [
    needsTransmission,
    needsClientInfo,
    (fieldsByCategory.get('matter')?.length ?? 0) > 0,
    (fieldsByCategory.get('recipient')?.length ?? 0) > 0,
  ].filter(Boolean);

  return {
    needsTransmission,
    needsClientInfo,
    transmissionCTA,
    categorizedFields,
    issueCount: issues.length,
    totalMissingFields: allMissingFields.length,
  };
}

// ============================================================================
// Display Helpers
// ============================================================================

const CATEGORY_LABELS: Record<MissingFieldCategory, string> = {
  transmission: 'Delivery Details',
  client: 'Client Information',
  matter: 'Matter Details',
  recipient: 'Recipient Details',
  other: 'Other Fields',
};

export function getCategoryLabel(category: MissingFieldCategory): string {
  return CATEGORY_LABELS[category];
}

// ============================================================================
// Client Field Mapping (Context Key → Form Field Name)
// ============================================================================

/**
 * Maps context keys (e.g., 'clientFirstName') to ClientForm field names (e.g., 'first_name').
 * Used to highlight specific fields in the client info modal.
 */
const CONTEXT_KEY_TO_FORM_FIELD: Record<string, string> = {
  clientName: 'name',
  clientFirstName: 'first_name',
  clientLastName: 'last_name',
  clientEmail: 'email',
  clientDateOfBirth: 'date_of_birth',
  clientAddress1: 'address.line1',
  clientAddress2: 'address.line2',
  clientAddress3: 'address.line3',
  clientCity: 'address.city',
  clientState: 'address.state',
  clientZip: 'address.postcode',
  clientLastFourOfSsn: 'last_four_of_ssn',
  clientSsn: 'ssn',
  // Note: clientPhone maps to phone_numbers which has special handling
  // clientAddress is a composite, not a single field
  // HIPAA signature fields are handled separately in the form
};

/**
 * Extract missing client field names from validation status.
 * Returns field names compatible with ClientForm (e.g., 'first_name', 'address.line1').
 */
export function getMissingClientFormFields(
  validationStatus: PackageValidationStatus,
): string[] {
  const allMissingFields: MissingFieldInfo[] =
    validationStatus.componentResults.flatMap((result) =>
      Object.values(result.missingFields),
    );

  const formFields: string[] = [];

  allMissingFields.forEach((field) => {
    if (!field.contextKey) return;

    // Only process client-related context keys
    if (!field.contextKey.toLowerCase().startsWith('client')) return;

    const formFieldName = CONTEXT_KEY_TO_FORM_FIELD[field.contextKey];
    if (formFieldName && !formFields.includes(formFieldName)) {
      formFields.push(formFieldName);
    }
  });

  return formFields;
}
